import { Middleware } from '@reduxjs/toolkit';
import { analytics } from '@lib/analytics';

export const analyticsMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  // Get action type
  const { type, payload } = action;

  try {
    // Authentication events
    if (type === 'auth/loginSuccess') {
      const user = payload?.data || payload;
      analytics.login(payload?.loginBy || 'email');
    }

    if (type === 'auth/registerSuccess') {
      analytics.signup('email');
    }

    if (type === 'auth/logout') {
      analytics.logout();
    }

    // Subscription events
    if (type.includes('subscribe') && type.includes('Success')) {
      const performer = payload?.performer || payload;
      analytics.subscribe(
        performer?._id || performer?.performerId,
        performer?.name || performer?.username || 'unknown',
        performer?.price || 0
      );
    }

    if (type.includes('unsubscribe') && type.includes('Success')) {
      const performer = payload?.performer || payload;
      analytics.unsubscribe(
        performer?._id || performer?.performerId,
        performer?.name || performer?.username || 'unknown'
      );
    }

    // Post/Feed interactions
    if (type.includes('likeSuccess') || type.includes('reactSuccess')) {
      analytics.likePost(payload?._id || payload?.itemId || 'unknown');
    }

    if (type.includes('unlikeSuccess') || type.includes('unreactSuccess')) {
      analytics.unlikePost(payload?._id || payload?.itemId || 'unknown');
    }

    if (type.includes('comment') && type.includes('Success')) {
      analytics.commentPost(payload?.objectId || payload?.postId || 'unknown');
    }

    // Follow/Unfollow events
    if (type.includes('follow') && type.includes('Success') && !type.includes('unfollow')) {
      const user = payload?.user || payload;
      analytics.followUser(
        user?._id || payload?.userId || 'unknown',
        user?.username || 'unknown'
      );
    }

    if (type.includes('unfollow') && type.includes('Success')) {
      const user = payload?.user || payload;
      analytics.unfollowUser(
        user?._id || payload?.userId || 'unknown',
        user?.username || 'unknown'
      );
    }

    // Tip/Payment events
    if (type.includes('sendTip') && type.includes('Success')) {
      analytics.sendTip(
        payload?.price || payload?.amount || 0,
        payload?.performerId || 'unknown',
        payload?.performerName || 'unknown'
      );
    }

    if (type.includes('purchasePost') || type.includes('purchaseVideo') || type.includes('purchaseGallery')) {
      if (type.includes('Success')) {
        analytics.purchaseContent(
          payload?.price || 0,
          payload?._id || payload?.itemId || 'unknown',
          payload?.type || 'content'
        );
      }
    }

    if (type.includes('addFunds') && type.includes('Success')) {
      analytics.addToWallet(payload?.amount || 0);
    }

    // Message events
    if (type === 'message/sendMessageSuccess' || type === 'streamChat/sendStreamMessageSuccess') {
      const conversationType = type.includes('stream') ? 'stream' : 'private';
      analytics.sendMessage(conversationType);
    }

    // Streaming events
    if (type.includes('goLive') && type.includes('Success')) {
      analytics.startStream();
    }

    if (type.includes('endStream') && type.includes('Success')) {
      analytics.endStream(payload?.duration || 0);
    }

    if (type === 'streaming/joinStreamSuccess' || type.includes('viewStream')) {
      const performer = payload?.performer || payload?.stream?.performerInfo || {};
      analytics.joinStream(
        performer?._id || 'unknown',
        performer?.username || performer?.name || 'unknown'
      );
    }

    // Search events
    if (type.includes('search') && type.includes('Success')) {
      analytics.search(
        payload?.q || payload?.keyword || '',
        payload?.type || payload?.category
      );
    }

    // Profile view events
    if (type === 'user/setPerformer' || type === 'performer/getPerformerSuccess') {
      const user = payload?.data || payload;
      if (user?._id) {
        analytics.viewProfile(user._id, user.username || 'unknown');
      }
    }

    // Post view events
    if (type === 'post/getPostSuccess' || type === 'feed/getPostSuccess') {
      const post = payload?.data || payload;
      if (post?._id) {
        analytics.viewPost(post._id, post.type || 'post');
      }
    }

    // Media upload events
    if (type.includes('upload') && type.includes('Success')) {
      const mediaType = type.includes('video') ? 'video' :
                       type.includes('photo') ? 'photo' :
                       type.includes('gallery') ? 'gallery' : 'media';
      analytics.uploadMedia(mediaType, payload?.count || 1);
    }

    // Profile/Settings update events
    if (type.includes('updateProfile') && type.includes('Success')) {
      analytics.updateProfile();
    }

    if (type.includes('updateSettings') && type.includes('Success')) {
      analytics.updateSettings(payload?.type || 'general');
    }

    // Gallery events
    if (type.includes('gallery') && type.includes('Success')) {
      const gallery = payload?.data || payload;
      if (type.includes('purchase')) {
        analytics.purchaseGallery(gallery?._id || 'unknown', gallery?.price || 0);
      } else if (type.includes('view') || type.includes('get')) {
        analytics.viewGallery(
          gallery?._id || 'unknown',
          gallery?.performerId || 'unknown'
        );
      }
    }

    // Product events
    if (type.includes('product') && type.includes('Success')) {
      const product = payload?.data || payload;
      if (type.includes('purchase')) {
        analytics.purchaseProduct(
          product?._id || 'unknown',
          product?.price || 0,
          payload?.quantity || 1
        );
      } else if (type.includes('view') || type.includes('get')) {
        analytics.viewProduct(
          product?._id || 'unknown',
          product?.performerId || 'unknown'
        );
      }
    }

    // Video events
    if (type.includes('video') && type.includes('Success')) {
      const video = payload?.data || payload;
      if (type.includes('purchase')) {
        analytics.purchaseVideo(video?._id || 'unknown', video?.price || 0);
      } else if (type.includes('play')) {
        analytics.playVideo(video?._id || 'unknown', video?.performerId || 'unknown');
      }
    }

    // Payout events
    if (type.includes('payout') && type.includes('Success') && type.includes('request')) {
      analytics.requestPayout(
        payload?.requestTokens || payload?.amount || 0,
        payload?.paymentAccountType || 'unknown'
      );
    }

    // Post creation
    if (type.includes('createPost') && type.includes('Success')) {
      const post = payload?.data || payload;
      analytics.createPost(
        post?.type || 'post',
        !!(post?.fileIds?.length || post?.teaserFileIds?.length)
      );
    }

    // Post deletion
    if (type.includes('deletePost') && type.includes('Success')) {
      analytics.deletePost(payload?.postId || payload?._id || 'unknown');
    }

    // Report content
    if (type.includes('report') && type.includes('Success')) {
      analytics.reportContent(
        payload?.targetId || 'unknown',
        payload?.target || 'content',
        payload?.reason || 'unknown'
      );
    }

    // Block/Unblock user
    if (type.includes('block') && type.includes('Success')) {
      const userId = payload?.targetId || payload?.userId || 'unknown';
      if (type.includes('unblock')) {
        analytics.unblockUser(userId);
      } else {
        analytics.blockUser(userId);
      }
    }

    // Performer registration
    if (type.includes('registerPerformer') && type.includes('Success')) {
      analytics.becomePerformer();
    }

    // Banking info update
    if (type.includes('updateBanking') && type.includes('Success')) {
      analytics.updateBankingInfo();
    }

    // Subscription price update
    if (type.includes('updateSubscription') && type.includes('Success')) {
      analytics.updateSubscriptionPrice(
        payload?.monthlyPrice || 0,
        'monthly'
      );
      if (payload?.yearlyPrice) {
        analytics.updateSubscriptionPrice(
          payload?.yearlyPrice || 0,
          'yearly'
        );
      }
    }

    // Error events
    if (type.includes('Fail') || type.includes('Error')) {
      const errorMessage = payload?.message || payload?.error || 'Unknown error';
      analytics.error(type.replace('Fail', '').replace('Error', ''), errorMessage);
    }

  } catch (error) {
    // Silently fail - don't break the app if analytics fails
    console.error('Analytics middleware error:', error);
  }

  return result;
};
